import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { Grid } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@mui/icons-material/Close";

import { updateTotalRefund } from "src/api/cancelOrder";

import font from "src/styles/font";
import device from "src/styles/device";
import { Black, DarkGrey, White01 } from "src/styles/color";
import { checkNotEmpty } from "src/common";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const SpanTitle = styled.div`
  color: ${Black};
  font-size: ${font.size.md};
  font-weight: 600;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const SpanLabel = styled.div`
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const SpanRemark = styled.div`
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;
  color: ${DarkGrey};

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
  }
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WrapperPrice = styled.div`
  width: max-content;
  display: flex;
  align-items: center;
  /* justify-content: space-between; */
  gap: 16px;

  .MuiTextField-root {
    max-width: 200px;
  }
  .MuiOutlinedInput-input {
    text-align: right;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }

  /* read-only */
  .MuiInputBase-root.Mui-readOnly {
    background-color: ${White01};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
    padding: 24,
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const EditRefundDialog = ({
  orderDetail,
  onClose,
  onSuccess,
  setIsLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const defaultData = {
    total: 0,
    remark: "",
  };
  const [updateOrder, setUpdateOrder] = useState(defaultData);

  useEffect(() => {
    if (!orderDetail) return;

    setUpdateOrder({
      ...defaultData,
      total: orderDetail?.payment?.total,
      remark: orderDetail?.payment?.remark,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  // eslint-disable-next-line no-unused-vars
  const isFormValid = ["total"].every((required) => {
    let objVal = updateOrder[required];
    return checkNotEmpty(objVal);
  });

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const updateParams = {
        ...updateOrder,
      };

      await updateTotalRefund(orderDetail?._id, updateParams);
      enqueueSnackbar(`Cancel Order Success!`, {
        variant: "success",
      });
      await onSuccess();
    } catch (err) {
      console.error("err:", err.response);
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        แก้ไขจำนวนเงิน การคืนเงิน
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={{ sm: 3, xs: 2 }}>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12}>
              <WrapperBox>
                <SpanLabel>จำนวนเงินที่คืน</SpanLabel>

                <WrapperPrice>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    onChange={(e) => {
                      setUpdateOrder({
                        ...updateOrder,
                        total: e.target.value,
                      });
                    }}
                    value={updateOrder.total}
                    error={!checkNotEmpty(updateOrder.total)}
                    // helperText={
                    //   !!formErrors[`promo_start_time`]
                    //     ? formErrors[`promo_start_time`].message
                    //     : ""
                    // }
                  />
                  <SpanLabel>บาท</SpanLabel>
                </WrapperPrice>
              </WrapperBox>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <SpanTitle mb={8}>หมายเหตุ</SpanTitle>

            <StyledTextField
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              onChange={(e) => {
                setUpdateOrder({
                  ...updateOrder,
                  remark: e.target.value,
                });
              }}
              value={updateOrder?.remark}
              placeholder="กรณียอดคืนไม่ตรงกับยอดชำระ กรุณาระบุเหตุผลในช่องหมายเหตุ"
            />
          </Grid>

          <Grid item xs={12}>
            <SpanRemark>
              - ชื่อบัญชีที่รับเงินคืนจะต้องเป็นชื่อเดียวกับชื่อที่โอนเข้า
              บริษัทฯเท่านั้น
            </SpanRemark>
            <SpanRemark>- ใช้เวลาดำเนินการ 7-14 วันทำการ</SpanRemark>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          sx={{
            width: "100%",
            maxWidth: 180,
          }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          disabled={!isFormValid}
          // disabled={true}
          sx={{
            width: "100%",
            maxWidth: 180,
          }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditRefundDialog;
